import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import EditGatewaySkuDialog from "../../components/product-manager/EditGatewaySkuDialog";
import { GATEWAY_SKU_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import { useBookmarkableHubState } from "../../hooks/useBookmarkableHubState";
import { useGatewaySkus } from "../../hooks/useGatewaySkus";
import { apiService } from "../../api/services/apiService";
import type { GatewaySku } from "../../types";
import { GatewaySkuStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";
import { gatewayTypeLabel } from "../../utils/gatewayType";

const SKU_STATUS_OPTIONS = Object.values(GatewaySkuStatus);

function filterGatewaySkuByHubState(
  items: GatewaySku[],
  state: ReturnType<typeof useBookmarkableHubState>,
): GatewaySku[] {
  let result = items;

  if (state.debouncedQ) {
    const query = state.debouncedQ.toLowerCase();
    result = result.filter((item) => {
      const fields = [
        item.gatewayProductIdentifier,
        item.gatewayProductName,
        item.gatewayCommonName,
        item.gatewayId,
        item.envName,
        gatewayTypeLabel(item.gatewayType),
      ];
      return fields.some((f) => (f ?? "").toLowerCase().includes(query));
    });
  }

  if (state.statuses.length) {
    result = result.filter((item) => state.statuses.includes(item.skuStatus));
  }

  return result;
}

const GatewaySkuHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState({ statusOptions: SKU_STATUS_OPTIONS });
  const { gatewaySkus: fetched, loading, error, refresh } = useGatewaySkus();
  const [gatewaySkus, setGatewaySkus] = useState<GatewaySku[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setGatewaySkus(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () => filterGatewaySkuByHubState(gatewaySkus, hubState),
    [gatewaySkus, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editItem = useMemo(
    () => gatewaySkus.find((g) => g.uuid === hubState.editUuid) ?? null,
    [gatewaySkus, hubState.editUuid],
  );

  const handleToggleActive = async (item: GatewaySku) => {
    setToggleLoadingId(item.uuid);
    const newStatus =
      item.skuStatus === GatewaySkuStatus.ACTIVE
        ? GatewaySkuStatus.INACTIVE
        : GatewaySkuStatus.ACTIVE;
    try {
      const updated = await apiService.updateGatewaySku(item.uuid, { skuStatus: newStatus });
      setGatewaySkus((prev) =>
        prev.map((g) => (g.uuid === updated.uuid ? updated : g)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update SKU status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Gateway SKU Hub"
        hubDescription="Manage gateway product SKUs with individual and bulk create, update, and enable or disable. Products become subscribable when linked to an active gateway SKU."
        addPath={pmHubAddPath("gatewaySku")}
        addLabel="Add Gateway SKU"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        statusOptions={SKU_STATUS_OPTIONS}
      >
        {paginated.length === 0 && !loading ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No gateway SKUs match your filters.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {paginated.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.uuid}>
                <EntityAssetCard
                  title={item.gatewayCommonName}
                  subtitle={`${gatewayTypeLabel(item.gatewayType)} · ${item.gatewayId} · ${item.envName}`}
                  status={item.skuStatus}
                  enabled={item.skuStatus === GatewaySkuStatus.ACTIVE}
                  onEdit={() => hubState.setEditUuid(item.uuid)}
                  onToggleEnabled={() => handleToggleActive(item)}
                  toggleLoading={toggleLoadingId === item.uuid}
                  onDelete={async () => {
                    await apiService.deleteGatewaySku(item.uuid);
                    setGatewaySkus((prev) => prev.filter((g) => g.uuid !== item.uuid));
                    refresh();
                  }}
                  metadata={
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.gatewayProductIdentifier}
                    </Typography>
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={GATEWAY_SKU_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditGatewaySkuDialog
        gatewaySku={editItem}
        open={Boolean(editItem)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) => {
          setGatewaySkus((prev) =>
            prev.map((g) => (g.uuid === updated.uuid ? updated : g)),
          );
        }}
      />
    </>
  );
};

export default GatewaySkuHubPage;
